import React, { useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
	Card,
	CardContent,
	Typography,
	Box,
	Tabs,
	Tab,
	FormControl,
	Select,
	MenuItem
} from '@material-ui/core';
import {
	Map as MapIcon,
	List as ListIcon
} from '@material-ui/icons';
import WorldMapVisualization from './WorldMapVisualization';
import ClusterOverviewTable from './ClusterOverviewTable';
import { getThemeColors } from '../utils/themeUtils';
import { getTagColor } from '../../cluster/components/ClusterTagsInput';

const useStyles = makeStyles(theme => ({
	card: {
		backgroundColor: '#1e293b',
		borderRadius: '8px',
		border: '1px solid #334155',
		boxShadow: 'none',
		display: 'flex',
		flexDirection: 'column',
		height: '100%'
	},
	cardContent: {
		padding: theme.spacing(3),
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		'&:last-child': {
			paddingBottom: theme.spacing(3)
		}
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: theme.spacing(1.5),
	},
	title: {
		fontSize: '16px',
		fontWeight: 600,
		color: '#f8fafc'
	},
	tabs: {
		minHeight: '32px',
		'& .MuiTabs-indicator': {
			backgroundColor: '#1C75D2',
			height: '2px'
		}
	},
	tab: {
		minHeight: '32px',
		minWidth: 'auto',
		padding: '6px 12px',
		textTransform: 'none',
		fontSize: '13px',
		fontWeight: 500,
		color: '#94a3b8',
		'&.Mui-selected': {
			color: '#1C75D2'
		},
		'& .MuiTab-wrapper': {
			flexDirection: 'row',
			gap: '6px'
		}
	},
	tabIcon: {
		fontSize: '16px',
		marginBottom: '0 !important'
	},
	// MUI colours the select arrow from the app-wide palette, which is independent of
	// the dashboard's themeMode; inherit so it tracks the select's own text colour.
	filterSelectIcon: {
		color: 'inherit'
	},
	contentContainer: {
		flex: 1,
		marginTop: theme.spacing(1),
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		maxHeight: '450px'
	},
	mapWrapper: {
		width: '100%',
		height: '100%',
		position: 'relative',
		backgroundColor: '#0f172a',
		borderRadius: '8px',
		overflow: 'hidden',
		border: '1px solid #1e293b'
	},
	tableWrapper: {
		width: '100%',
		height: '100%',
		maxHeight: '100%',
		overflowY: 'auto',
		overflowX: 'hidden',
		'&::-webkit-scrollbar': {
			width: '8px'
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: ({ colors }) => colors.scrollbar.track,
			borderRadius: '4px'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: ({ colors }) => colors.scrollbar.thumb,
			borderRadius: '4px',
			'&:hover': {
				backgroundColor: ({ colors }) => colors.scrollbar.thumbHover
			}
		}
	},
	tableInner: {
		// Remove card styling from inner table
		'& > div': {
			backgroundColor: 'transparent',
			border: 'none',
			boxShadow: 'none',
			borderRadius: 0
		},
		'& .MuiCardContent-root': {
			padding: 0
		}
	}
}));

const ClusterVisualization = ({ clusters, themeMode = 'dark', userView = false, user = null }) => {
	const colors = getThemeColors(themeMode);
	const classes = useStyles({ colors });
	const [activeTab, setActiveTab] = useState(0);
	const [selectedProvider, setSelectedProvider] = useState('');
	const [selectedTag, setSelectedTag] = useState('');

	// The provider/tag filters share their look; derive it from the theme so both
	// follow light/dark mode instead of being pinned to the dark palette.
	const filterSelectStyle = {
		fontSize: '12px',
		fontWeight: 500,
		color: colors.text.primary,
		backgroundColor: colors.background.tertiary,
		borderRadius: '6px',
		height: '28px'
	};

	const filterMenuProps = {
		anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
		transformOrigin: { vertical: 'top', horizontal: 'left' },
		getContentAnchorEl: null,
		PaperProps: {
			style: {
				backgroundColor: colors.background.secondary,
				borderRadius: '6px',
				border: `1px solid ${colors.border.secondary}`,
				boxShadow: themeMode === 'dark'
					? '0 4px 16px rgba(0, 0, 0, 0.3)'
					: '0 4px 16px rgba(15, 23, 42, 0.12)',
				marginTop: '2px',
				maxHeight: '220px'
			}
		}
	};

	// Check if user has 'demo' role (check both 'role' and 'roles' fields)
	const isDemoUser = user && (
		(user.role && Array.isArray(user.role) && user.role.some(r =>
			typeof r === 'string' && r.toLowerCase().includes('demo')
		)) ||
		(user.roles && Array.isArray(user.roles) && user.roles.some(r =>
			typeof r === 'string' && r.toLowerCase().includes('demo')
		))
	);

	// Set title based on demo user status
	const mapTitle = isDemoUser ? 'Heidi AI Supercomputers' : 'Global Cluster Distribution';

	// Derive unique providers and tags from clusters
	const { providers, tags } = useMemo(function() {
		var provSet = {};
		var tagSet = {};
		if (clusters) {
			for (var i = 0; i < clusters.length; i++) {
				var c = clusters[i];
				if (c.provider) provSet[c.provider] = true;
				if (c.tags && c.tags.length) {
					for (var j = 0; j < c.tags.length; j++) {
						tagSet[c.tags[j]] = true;
					}
				}
			}
		}
		return {
			providers: Object.keys(provSet).sort(),
			tags: Object.keys(tagSet).sort()
		};
	}, [clusters]);

	// Transform all clusters to show as green/available for user dashboard
	// Then apply provider/tag filters
	var baseClusters = userView ? clusters.map(cluster => ({
		...cluster,
		status: 'available',
		color: '#22c55e'
	})) : clusters;

	const displayClusters = useMemo(function() {
		var filtered = baseClusters;
		if (selectedProvider) {
			filtered = filtered.filter(function(c) { return c.provider === selectedProvider; });
		}
		if (selectedTag) {
			filtered = filtered.filter(function(c) { return c.tags && c.tags.indexOf(selectedTag) !== -1; });
		}
		return filtered;
	}, [baseClusters, selectedProvider, selectedTag]);

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	return (
		<Card 
			className={classes.card}
			style={{
				backgroundColor: colors.card.background,
				border: `1px solid ${colors.card.border}`,
				boxShadow: colors.card.shadow
			}}
		>
			<CardContent className={classes.cardContent}>
			<Box className={classes.header}>
				<Typography className={classes.title} style={{ color: colors.text.primary }}>
					{activeTab === 0 ? mapTitle : 'Clusters'}
				</Typography>

				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					{/* Provider filter */}
					{providers.length > 1 && (
						<FormControl variant="outlined" size="small">
							<Select
								value={selectedProvider}
								onChange={function(e) { setSelectedProvider(e.target.value); }}
								displayEmpty
								classes={{ icon: classes.filterSelectIcon }}
								style={{ ...filterSelectStyle, minWidth: '110px' }}
								MenuProps={filterMenuProps}
							>
								<MenuItem value="" style={{ fontSize: '12px', color: colors.text.tertiary }}>
									All Providers
								</MenuItem>
								{providers.map(function(p) {
									return (
										<MenuItem key={p} value={p} style={{ fontSize: '12px', color: colors.text.primary }}>
											{p.toUpperCase()}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>
					)}

					{/* Tag filter */}
					{tags.length > 0 && (
						<FormControl variant="outlined" size="small">
							<Select
								value={selectedTag}
								onChange={function(e) { setSelectedTag(e.target.value); }}
								displayEmpty
								classes={{ icon: classes.filterSelectIcon }}
								style={{ ...filterSelectStyle, minWidth: '100px' }}
								MenuProps={filterMenuProps}
								renderValue={function(val) {
									if (!val) return <span style={{ color: colors.text.tertiary }}>All Tags</span>;
									var tc = getTagColor(val);
									return (
										<span style={{
											backgroundColor: tc.bg,
											color: tc.text,
											padding: '1px 6px',
											borderRadius: '3px',
											fontWeight: 500,
											fontSize: '11px',
										}}>
											{val}
										</span>
									);
								}}
							>
								<MenuItem value="" style={{ fontSize: '12px', color: colors.text.tertiary }}>
									All Tags
								</MenuItem>
								{tags.map(function(t) {
									var tc = getTagColor(t);
									return (
										<MenuItem key={t} value={t} style={{
											fontSize: '12px',
											color: tc.text,
											backgroundColor: tc.bg,
											fontWeight: 500,
											margin: '2px 6px',
											borderRadius: '4px',
										}}>
											{t}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>
					)}

					<Tabs
						value={activeTab}
						onChange={handleTabChange}
						className={classes.tabs}
					>
						<Tab
							label="Map"
							icon={<MapIcon className={classes.tabIcon} style={{ color: activeTab === 0 ? colors.accent.blue : colors.text.secondary }} />}
							className={classes.tab}
							style={{ color: activeTab === 0 ? colors.accent.blue : colors.text.secondary }}
						/>
						{!userView && (
							<Tab
								label="List"
								icon={<ListIcon className={classes.tabIcon} style={{ color: activeTab === 1 ? colors.accent.blue : colors.text.secondary }} />}
								className={classes.tab}
								style={{ color: activeTab === 1 ? colors.accent.blue : colors.text.secondary }}
							/>
						)}
					</Tabs>
				</div>
				</Box>

				<div 
					className={classes.contentContainer}
					style={{ maxHeight: userView ? 'none' : '450px' }}
				>
					{activeTab === 0 ? (
						<div className={classes.mapWrapper} style={{ backgroundColor: colors.map.background, border: `1px solid ${colors.border.primary}` }}>
							<WorldMapVisualization clusters={displayClusters} embedded themeMode={themeMode} userView={userView} />
						</div>
					) : (
						<div className={classes.tableWrapper}>
							<div className={classes.tableInner}>
								<ClusterOverviewTable clusters={displayClusters} embedded themeMode={themeMode} />
							</div>
						</div>
					)}
				</div>
			</CardContent>
		</Card>
	);
};

// Memoize component to prevent unnecessary re-renders
// Only re-render if clusters data actually changes (deep comparison)
const arePropsEqual = (prevProps, nextProps) => {
	// Compare theme mode
	if (prevProps.themeMode !== nextProps.themeMode) {
		return false;
	}
	
	// Compare userView
	if (prevProps.userView !== nextProps.userView) {
		return false;
	}
	
	// Compare clusters array length
	if (prevProps.clusters?.length !== nextProps.clusters?.length) {
		return false;
	}
	
	// If both are empty/null, they're equal
	if (!prevProps.clusters && !nextProps.clusters) {
		return true;
	}
	
	// Deep comparison of cluster data (id, state, metrics)
	// This prevents re-render when clusters array reference changes but content is same
	if (prevProps.clusters && nextProps.clusters) {
		for (let i = 0; i < prevProps.clusters.length; i++) {
			const prev = prevProps.clusters[i];
			const next = nextProps.clusters[i];
			
			// Compare critical fields that would need UI update
			// Note: state (not status) is the field that contains cluster state
			if (prev.id !== next.id || 
				prev.state !== next.state ||
				prev.status !== next.status ||
				prev.name !== next.name ||
				JSON.stringify(prev.location) !== JSON.stringify(next.location)) {
				return false;
			}
		}
	}
	
	return true;
};

export default React.memo(ClusterVisualization, arePropsEqual);
